import React, { useState, useEffect } from 'react';
import { toggleLoading } from "../services/helpers";
import { jobGetCode } from "../services/job";
import { Button } from "react-bootstrap";
import Wizard from "./Wizard";
import ReturnPaymentStep1 from "./ReturnPaymentStep1";
import ReturnPaymentStep2 from "./ReturnPaymentStep2";
import ReturnPaymentStep3 from "./ReturnPaymentStep3";
import messages from "../views/Dashboard/Dashboard.messages";
import PropTypes from "prop-types";
import { injectIntl } from "react-intl";
import useForm from "../hooks/useForm";
import useUserAccountSettings from "../hooks/useUserAccountSettings";
import useBankAccount from "../hooks/useBankAccount";
import useUserAccount from "../hooks/useUserAccount";

const defaultSettings = {
    bankAccountType: '',
    bankAccountNumber: '',
    bankAccountHolderName: '',
    bankAccountBic: ''
};

const defaultJobSearch = {
    caseNumber: '',
    onlineId: ''
};

const OverpaidWizard = (props) => {
    const lng = props.intl.formatMessage;
    const {
        settings, getUserAccountCompanySettings, getUserAccountSettings,
        updateUserAccountCompanySettings, updateUserAccountSettings
    } = useUserAccountSettings();
    const { postBankAccount } = useBankAccount();
    const { userIsCompany, userIsAuthenticated, userHasAccount } = useUserAccount();
    const userIdSignIn = userHasAccount && userIsAuthenticated();
    const { form: jobSearchForm, handleChange: handleJobSearchFormChange } = useForm(defaultJobSearch);
    const { form, handleChange, validateForm } = useForm(userIdSignIn ? settings : defaultSettings);
    const [saveSettings, setSaveSettings] = useState(userIdSignIn);
    const [returnPaymentStep, setReturnPaymentStep] = useState(-1);
    const [returnPaymentError, setReturnPaymentError] = useState({
        message: '',
        type: 'light'
    });

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                if (userIsCompany()) {
                    await getUserAccountCompanySettings();
                } else {
                    await getUserAccountSettings();
                }
            } catch (error) {
                console.error("Error fetching user account settings:", error);
            }
        }
        if (userIdSignIn) {
            void fetchSettings();
        }
    }, [userIdSignIn]);

    // If job is given, move to next step from search step
    useEffect(() => {
        if (props.job && returnPaymentStep === 0 && props.showSearchStep) {
            returnPaymentMoveToNextStep();
        }
    }, [props.job, props.showSearchStep, returnPaymentStep]);

    // Handle steps data
    useEffect(() => {
        if (props.open === false) {
            resetReturnPaymentError();
            setReturnPaymentStep(-1);
        } else {
            setReturnPaymentStep(0);
        }
    }, [props.open]);

    /**
     * Function for closing the Return payment wizard
     * (Set the current step to -1)
     */
    const returnPaymentClose = () => {
        resetReturnPaymentError();
        setReturnPaymentStep(-1);
        props.onClose();
    };

    /**
     * Function for moving to next step in Return payment wizard
     */
    const returnPaymentMoveToNextStep = () => {
        resetReturnPaymentError();
        setReturnPaymentStep(returnPaymentStep + 1);
    };

    /**
     * Function for moving to previous step in Return payment wizard
     */
    const returnPaymentMoveToPreviousStep = () => {
        setReturnPaymentStep(returnPaymentStep - 1);
    }

    /**
     * Update user account settings if user is signed in and has chosen to save the bank account details
     * @returns {Promise<void>}
     */
    const updateSettings = async () => {
        if (userIsCompany()) {
            await updateUserAccountCompanySettings(form);
        } else {
            await updateUserAccountSettings(form);
        }
    }

    /**
     * Function for submitting the bank account number for returning an overpayment
     */
    const handleBankAccountSubmit = () => {
        // Get the form
        const formElement = document.getElementById('returnpayment-accountdata-form');
        if (formElement === null) {
            // Error: Form was not found
            setReturnPaymentError({
                message: lng(messages.unknownError),
                type: 'danger'
            });
            return
        }
        // Validate form data
        if (!validateForm(formElement)) {
            return
        }
        // Get the data for request
        const id = jobGetCode();
        const iban = form.bankAccountNumber ?? '';
        const name = form.bankAccountHolderName ?? '';
        const type = form.bankAccountType ?? '';
        const bic = form.bankAccountBic ?? '';

        if (id !== '' && iban !== '' && name !== '' && type !== '') {
            const overpaidData = {
                id,
                iban,
                name,
                type,
                user: {
                    name: '',
                    personid: ''
                }
            };

            if (bic !== '') {
                overpaidData['bic'] = bic;
            }

            void submitOverpaid(overpaidData);
        } else {
            // Error required data was not found
            setReturnPaymentError({
                message: lng(messages.unknownError),
                type: 'danger'
            });
        }
    };

    /**
     * Handle errors in overpayment submit
     * @param error
     */
    const handleOverpaidError = (error) => {
        // Get the error message from response
        const message = error.response?.data?.Message !== undefined ? error.response.data.Message.trim() : '';

        // Highlight the invalid field
        if (message !== 'Invalid bic') {
            // Highlight Account number field
            const element = document.getElementById('returnpayment-account-number');
            if (!element.classList.contains('is-invalid')) {
                element.classList.add('is-invalid');
            }
        } else {
            // Highlight BIC field
            const element = document.getElementById('returnpayment-account-bic');
            if (!element.classList.contains('is-invalid')) {
                element.classList.add('is-invalid');
            }
        }

        let displayError = '';

        // Set the error message according to the response
        switch(message) {
            case 'Bank account number is not iban':
            case 'Invalid IBAN (Format)':
                displayError = lng(messages.invalidIbanFormat);
                break;

            case 'Invalid Bankgiro':
                displayError = lng(messages.invalidBankgiroFormat);
                break;

            case 'Invalid Plusgirot':
            case 'Invalid PlusGirot':
                displayError = lng(messages.invalidPlusgirotFormat);
                break;

            case 'Invalid Bban':
                displayError = lng(messages.invalidBbanFormat);
                break;

            case 'Unable to get BIC from iban':
                displayError = lng(messages.bicFromIbanFail);
                break;

            case 'Invalid bic':
                displayError = lng(messages.bicCodeInvalid);
                break;

            case 'Operation in progress':
                displayError = lng(messages.overpaidRefundInProgress);
                break;

            default:
                displayError = lng(messages.defaultErrorMessage);
                break;
        }

        setReturnPaymentError({
            message: displayError,
            type: 'danger'
        });
    }

    /**
     * Reset return payment process errors
     */
    const resetReturnPaymentError = () => {
        setReturnPaymentError({
            message: '',
            type: 'light'
        });
    }

    /**
     * Sends a bank account number for returning an overpayment
     * @param overpaidData
     */
    const submitOverpaid = async (overpaidData) => {
        // Shows the loading animation "Saving..."
        toggleLoading(true, lng(messages.actionSaving));

        try {
            await postBankAccount(JSON.stringify(overpaidData));
            if (saveSettings) {
                await updateSettings();
            }
            returnPaymentClose();
            props.onBankAccountSubmitSuccess(overpaidData.id);
        } catch (error) {
            // Error: Payment return process failed, stay in wizard
            handleOverpaidError(error);
        } finally {
            toggleLoading(false);
        }
    }

    const resolveSteps = () => {
        const steps = [];
        if (props.showSearchStep) {
            steps.push(
                {
                    title: lng(messages.returnPaymentTitle),
                    returnText: lng(messages.goBack),
                    bodyContent: <ReturnPaymentStep1
                        onFormChange={handleJobSearchFormChange}
                        form={jobSearchForm}
                        error={props.jobSearchError}
                    />,
                    footerContent: <Button variant='primary' onClick={() => props.onJobSearchSubmit(jobSearchForm)}>
                        {lng(messages.nextStep)}
                    </Button>,
                    closeFunction: returnPaymentClose,
                    nextFunction: () => props.onJobSearchSubmit(jobSearchForm)
                }
            );
        }
        steps.push(
            {
                title: lng(messages.returnPaymentTitle),
                returnText: lng(messages.goBack),
                bodyContent: <ReturnPaymentStep2 />,
                footerContent: <Button variant='primary' onClick={returnPaymentMoveToNextStep}>{lng(messages.nextStep)}</Button>,
                closeFunction: returnPaymentClose,
                backFunction: returnPaymentMoveToPreviousStep,
                nextFunction: returnPaymentMoveToNextStep
            },
            {
                title: lng(messages.returnPaymentTitle),
                returnText: lng(messages.goBack),
                bodyContent: <ReturnPaymentStep3
                    error={returnPaymentError}
                    form={form}
                    onFormChange={handleChange}
                    saveSettings={saveSettings}
                    setSaveSettings={setSaveSettings}
                    showSaveSettings={userIdSignIn}
                />,
                footerContent: <Button variant='primary' onClick={handleBankAccountSubmit}>{lng(messages.send)}</Button>,
                closeFunction: returnPaymentClose,
                backFunction: returnPaymentMoveToPreviousStep,
                nextFunction: handleBankAccountSubmit
            }
        );
        return steps;
    }

    return (
        <Wizard
            currentStep={returnPaymentStep}
            steps={resolveSteps()}
        />
    );
};

// Property validation
OverpaidWizard.propTypes = {
    intl: PropTypes.object.isRequired,
    job: PropTypes.object,
    open: PropTypes.bool,
    onClose: PropTypes.func,
    onJobSearchSubmit: PropTypes.func,
    showSearchStep: PropTypes.bool.isRequired,
    jobSearchError: PropTypes.object,
    onBankAccountSubmitSuccess: PropTypes.func
};

export default injectIntl(OverpaidWizard);
